import Locale from "sap/ui/core/Locale";
import LocaleData from "sap/ui/core/LocaleData";
import Localization from "sap/base/i18n/Localization";

/**
 * Lista krajów oparta o UI5 `LocaleData.getTerritories()` (CLDR). Filtrujemy do
 * ISO 3166-1 alpha-2 (2-literowe kody, A-Z) — `getTerritories()` zwraca też kody
 * kontynentów (`001`, `EU`, `015`...) i grupy regionów, których nie chcemy w
 * dropdownie kraju.
 *
 * **Format kanoniczny w bazie:** ISO alpha-2 (`PL`, `DE`, `US`). Istniejące rekordy
 * z plain-text country (np. legacy "Polska") nie zmatchują się z `selectedKey` na
 * ComboBoxie — read mode pokaże raw value przez {@link lookupCountryName} fallback.
 *
 * @namespace computec.b2b.portal.model
 */

export interface CountryItem {
	code: string;
	name: string;
}

const ISO_ALPHA2_PATTERN = /^[A-Z]{2}$/;

function getTerritories(localeId: string): Record<string, string> {
	return LocaleData.getInstance(new Locale(localeId)).getTerritories();
}

function currentLocaleId(): string {
	return Localization.getLanguage();
}

/**
 * Posortowana po lokalnej nazwie lista krajów dla bieżącego locale (lub podanego).
 * Używana jako `items` ComboBox/Select w edit mode.
 */
export function getCountries(localeId?: string): CountryItem[] {
	const id = localeId ?? currentLocaleId();
	const territories = getTerritories(id);
	return Object.entries(territories)
		.filter(([code]) => ISO_ALPHA2_PATTERN.test(code))
		.map(([code, name]) => ({ code, name }))
		.sort((a, b) => a.name.localeCompare(b.name, id));
}

/**
 * Lookup nazwy kraju po kodzie ISO. Fallback: zwraca raw code (obsługuje legacy
 * dane z pełną nazwą — "Polska" przejdzie 1:1, bo nie matchuje 2-literowego kodu
 * w słowniku CLDR).
 */
export function lookupCountryName(code: string | null | undefined, localeId?: string): string {
	if (!code) {
		return "";
	}
	if (!ISO_ALPHA2_PATTERN.test(code)) {
		return code; // legacy / non-ISO value — passthrough
	}
	const territories = getTerritories(localeId ?? currentLocaleId());
	return territories[code] ?? code;
}
